import type { QueryClient } from '@tanstack/react-query';
import type { Project } from '@vd/types';
import { api } from '../../lib/api';
import type { ProjectRecordingMode } from './RecordingModeSection';

/**
 * Save what a walkthrough of this project records (TOADO-383).
 *
 * The saved value is taken from the PATCH response and written into the
 * project query, rather than left to a refetch to discover. Awaiting
 * `invalidateQueries` looked like it covered this, but a refetch that fails
 * is swallowed: the query keeps its old value, the save reports success, and
 * the control ends up showing the opposite of what was just saved with no way
 * to correct it - clicking the mode it displays sends nothing, because it
 * believes that is already the answer. Review round 2 reproduced that.
 *
 * The company's project list is still invalidated rather than written,
 * because that response is a different shape and the extension reads it for
 * more than this one field.
 *
 * Throws on failure. The caller owns telling somebody, and the control owns
 * giving the radio back.
 */
export async function saveProjectRecordingMode(
  qc: QueryClient,
  project: { id: string; companyId: string },
  mode: ProjectRecordingMode,
): Promise<Project> {
  const saved = await api.updateProject(project.id, { recordingMediaMode: mode });
  // Cancel first. A project read already in flight when the save lands - the
  // background refetch a stale settings page starts, say - resolves with the
  // value from before the save and overwrites this write, putting the control
  // back to displaying the opposite of what was just saved. Which is round
  // 2's symptom reached by a different route (review round 3).
  await qc.cancelQueries({ queryKey: ['project', project.id] });
  qc.setQueryData(['project', project.id], saved);
  await qc.invalidateQueries({ queryKey: ['projects', project.companyId] });
  return saved;
}
